import { FormBuilderDialog } from '@/components/FormBuilder/FormBuilderDialog'
import { FormTemplatePickerDialog } from '@/components/FormTemplates/FormTemplatePickerDialog'
import { FormCard as FormListCard } from '@/components/Forms/FormCard'
import { FormFolderSidebar } from '@/components/Forms/FormFolderSidebar'
import { FormManagementDialog as FormManagementWorkspaceDialog } from '@/components/Forms/FormManagementDialog'
import { FormTable as FormListTable } from '@/components/Forms/FormTable'
import type {
  ArchiveStatusFilter,
  FormFolderDraft,
  PublicationStatusFilter,
} from '@/components/Forms/types'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type {
  FormManagementTabId,
} from '@/contracts/form-management'
import type {
  FormFolderContract,
  FormListSort,
  FormStatusFilter,
  FormUiContract,
} from '@/contracts/questionnaire'
import { useAuth } from '@/lib/auth'
import {
  createFormFromTemplate,
  listFormTemplates,
} from '@/lib/data-client/form-templates'
import {
  createFormFolder,
  deleteFormFolder,
  getPersistentFormListState,
  getWorkspaceId,
  changeFormStatus,
  duplicateForm,
  listFormFolders,
  listForms,
  reorderFormFolders,
  saveFormListState,
  setFormFolderForms,
  updateFormFolder,
} from '@/lib/data-client/forms'
import { isApiDataClientMode } from '@/lib/data-client/mode'
import { primaryFormId } from '@/lib/mock-data'
import { hasFormAdminAccess } from '@/lib/permissions'
import { useSystemFeatureVisibility } from '@/lib/system-feature-visibility'
import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  FileText,
  LayoutGrid,
  List,
  Plus,
  Search,
  Sparkles,
} from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'

type FolderItem = FormFolderContract

function uniqueFormIds(formIds: string[]) {
  return Array.from(new Set(formIds))
}

function orderForms(forms: FormUiContract[], formOrder: string[]) {
  const orderIndex = new Map(formOrder.map((id, index) => [id, index]))
  return [...forms].sort((a, b) => {
    const aIndex = orderIndex.get(a.id) ?? Number.MAX_SAFE_INTEGER
    const bIndex = orderIndex.get(b.id) ?? Number.MAX_SAFE_INTEGER
    if (aIndex !== bIndex) return aIndex - bIndex
    return b.updatedAt.localeCompare(a.updatedAt)
  })
}

export default function FormsRoute() {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const canManageForms = hasFormAdminAccess(user)
  const featureVisibility = useSystemFeatureVisibility()
  const templatesEnabled = featureVisibility['forms.templates']
  const persistedFormState = useMemo(() => getPersistentFormListState(), [])
  const usesApiDataClient = useMemo(() => isApiDataClientMode(), [])
  const folderSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )
  const [keyword, setKeyword] = useState('')
  const [publicationStatus, setPublicationStatus] = useState<PublicationStatusFilter>('all')
  const [archiveStatus, setArchiveStatus] = useState<ArchiveStatusFilter>('active')
  const [sort, setSort] = useState<FormListSort>('updated_desc')
  const [view, setView] = useState<'grid' | 'list'>('grid')
  const { data: formsResult } = useQuery({
    queryKey: ['forms', keyword, sort],
    queryFn: () => listForms({ keyword, sort, limit: 100 }),
  })
  const { data: folderResult } = useQuery({
    queryKey: ['form-folders'],
    queryFn: listFormFolders,
  })
  const { data: templatesResult } = useQuery({
    queryKey: ['form-templates'],
    queryFn: listFormTemplates,
    enabled: canManageForms && templatesEnabled,
  })
  const data = formsResult?.data ?? []
  const formTemplates = templatesResult?.data ?? []
  const [folders, setFolders] = useState<FolderItem[]>(
    usesApiDataClient ? [] : persistedFormState.formFolders,
  )
  const [formOrder, setFormOrder] = useState<string[]>(
    usesApiDataClient ? [] : persistedFormState.formOrder,
  )
  const [activeFolderId, setActiveFolderId] = useState('all')
  const [deletedFormIds, setDeletedFormIds] = useState<string[]>(
    usesApiDataClient ? [] : persistedFormState.deletedFormIds,
  )
  const [editingFormId, setEditingFormId] = useState<string | null>(null)
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false)
  const [creatingTemplateId, setCreatingTemplateId] = useState<string | null>(null)
  const [managementDialog, setManagementDialog] = useState<{
    form: FormUiContract
    tab: FormManagementTabId
  } | null>(null)

  useEffect(() => {
    if (!folderResult?.data) return
    if (usesApiDataClient) {
      setFolders(folderResult.data)
      return
    }
    if (folders.length === 0) {
      setFolders(folderResult.data)
    }
  }, [folderResult?.data, folders.length, usesApiDataClient])

  useEffect(() => {
    if (activeFolderId === 'all') return
    if (folders.some((folder) => folder.id === activeFolderId)) return
    setActiveFolderId('all')
  }, [activeFolderId, folders])

  function persistListState(patch: {
    formOrder?: string[]
    deletedFormIds?: string[]
    formFolders?: FolderItem[]
  }) {
    saveFormListState(patch)
    void queryClient.invalidateQueries({ queryKey: ['forms'] })
    void queryClient.invalidateQueries({ queryKey: ['form-folders'] })
  }

  const activeFolder = folders.find((folder) => folder.id === activeFolderId)
  const visibleForms = useMemo(
    () => data.filter((form) => !deletedFormIds.includes(form.id)),
    [data, deletedFormIds],
  )
  const orderedVisibleForms = useMemo(
    () => orderForms(visibleForms, formOrder),
    [formOrder, visibleForms],
  )

  function moveFormToFolder(formId: string, folderId: string) {
    if (!canManageForms) return
    setFolders((current) => {
      const next = current.map((folder) => {
        const withoutForm = folder.formIds.filter((id) => id !== formId)
        return folder.id === folderId
          ? { ...folder, formIds: uniqueFormIds([...withoutForm, formId]) }
          : { ...folder, formIds: withoutForm }
      })
      if (usesApiDataClient) {
        void Promise.all(
          next.map((folder) => setFormFolderForms(folder.id, uniqueFormIds(folder.formIds))),
        )
          .then(() => {
            void queryClient.invalidateQueries({ queryKey: ['form-folders'] })
          })
          .catch(() => {
            toast.error('資料夾分類更新失敗，請重新整理後再試。')
            void queryClient.invalidateQueries({ queryKey: ['form-folders'] })
          })
      } else {
        persistListState({ formFolders: next })
      }
      return next
    })
    setActiveFolderId(folderId)
  }

  async function createFolderFromSidebar(draft: FormFolderDraft) {
    if (!canManageForms) return
    if (usesApiDataClient) {
      try {
        const created = await createFormFolder({
          name: draft.name,
          color: '#6366F1',
          formIds: uniqueFormIds(draft.formIds),
        })
        setFolders((current) => {
          const withoutDuplicate = current.filter((folder) => folder.id !== created.id)
          return [...withoutDuplicate, created].sort((left, right) => {
            if (left.orderIndex !== right.orderIndex) return left.orderIndex - right.orderIndex
            return left.name.localeCompare(right.name)
          })
        })
        setActiveFolderId(created.id)
        void queryClient.invalidateQueries({ queryKey: ['form-folders'] })
        toast.success('資料夾已建立。')
      } catch {
        toast.error('資料夾建立失敗，請重新整理後再試。')
        void queryClient.invalidateQueries({ queryKey: ['form-folders'] })
      }
      return
    }
    const id = `folder-${crypto.randomUUID()}`
    setFolders((current) => {
      const next = [
        ...current,
        {
          id,
          workspaceId: visibleForms[0]?.workspaceId ?? getWorkspaceId(),
          name: draft.name,
          color: '#6366F1',
          orderIndex: current.length,
          formIds: uniqueFormIds(draft.formIds),
        },
      ]
      persistListState({ formFolders: next })
      return next
    })
    setActiveFolderId(id)
  }

  function updateFolderFromSidebar(folderId: string, draft: FormFolderDraft) {
    if (!canManageForms) return
    setFolders((current) => {
      const next = current.map((folder) =>
        folder.id === folderId
          ? { ...folder, name: draft.name, formIds: uniqueFormIds(draft.formIds) }
          : folder,
      )
      if (usesApiDataClient) {
        void updateFormFolder(folderId, {
          name: draft.name,
          formIds: uniqueFormIds(draft.formIds),
        })
          .then(() => {
            void queryClient.invalidateQueries({ queryKey: ['form-folders'] })
            toast.success('資料夾已更新。')
          })
          .catch(() => {
            toast.error('資料夾更新失敗，請重新整理後再試。')
            void queryClient.invalidateQueries({ queryKey: ['form-folders'] })
          })
      } else {
        persistListState({ formFolders: next })
      }
      return next
    })
  }

  function removeFolderFromSidebar(folderId: string) {
    if (!canManageForms) return
    const next = folders.filter((folder) => folder.id !== folderId)
    setFolders(next)
    if (usesApiDataClient) {
      void deleteFormFolder(folderId)
        .then(() => {
          void queryClient.invalidateQueries({ queryKey: ['form-folders'] })
          toast.success('資料夾已移除。')
        })
        .catch(() => {
          toast.error('資料夾移除失敗，請重新整理後再試。')
          void queryClient.invalidateQueries({ queryKey: ['form-folders'] })
        })
    } else {
      persistListState({ formFolders: next })
    }
    if (activeFolderId === folderId) setActiveFolderId('all')
  }

  function reorderFoldersFromSidebar(activeId: string, overId: string) {
    if (!canManageForms) return
    setFolders((current) => {
      const oldIndex = current.findIndex((folder) => folder.id === activeId)
      const newIndex = current.findIndex((folder) => folder.id === overId)
      if (oldIndex < 0 || newIndex < 0) return current
      const next = arrayMove(current, oldIndex, newIndex).map((folder, orderIndex) => ({
        ...folder,
        orderIndex,
      }))
      if (usesApiDataClient) {
        void reorderFormFolders(next.map((folder) => folder.id))
          .then(() => {
            void queryClient.invalidateQueries({ queryKey: ['form-folders'] })
          })
          .catch(() => {
            toast.error('資料夾排序更新失敗，請重新整理後再試。')
            void queryClient.invalidateQueries({ queryKey: ['form-folders'] })
          })
      } else {
        persistListState({ formFolders: next })
      }
      return next
    })
  }

  const filtered = useMemo(
    () =>
      orderedVisibleForms.filter((form) => {
        const matchKeyword = form.title.toLowerCase().includes(keyword.toLowerCase())
        const matchPublicationStatus =
          publicationStatus === 'all' || form.status === publicationStatus
        const matchArchiveStatus =
          archiveStatus === 'all' ||
          (archiveStatus === 'archived' ? form.status === 'archived' : form.status !== 'archived')
        const matchFolder = activeFolderId === 'all' || activeFolder?.formIds.includes(form.id)
        return matchKeyword && matchPublicationStatus && matchArchiveStatus && matchFolder
      }),
    [activeFolder?.formIds, activeFolderId, archiveStatus, keyword, orderedVisibleForms, publicationStatus],
  )

  function reorderForms(event: DragEndEvent) {
    if (!canManageForms) return
    const { active, over } = event
    if (!over || active.id === over.id) return

    const renderedIds = filtered.map((form) => form.id)
    const oldIndex = renderedIds.indexOf(String(active.id))
    const newIndex = renderedIds.indexOf(String(over.id))
    if (oldIndex < 0 || newIndex < 0) return

    const nextRenderedIds = arrayMove(renderedIds, oldIndex, newIndex)
    const nextRenderedSet = new Set(nextRenderedIds)

    setFormOrder((current) => {
      const baseIds = orderForms(visibleForms, current).map((form) => form.id)
      let cursor = 0
      const next = baseIds.map((id) => (nextRenderedSet.has(id) ? nextRenderedIds[cursor++] : id))
      persistListState({ formOrder: next })
      return next
    })
  }

  function previewForm(formId: string) {
    window.open(`/to/${formId}?preview=1`, '_blank', 'noopener,noreferrer')
  }

  function openFormManagement(form: FormUiContract, tab: FormManagementTabId = 'notifications') {
    setManagementDialog({ form, tab })
  }

  async function duplicateSelectedForm(form: FormUiContract) {
    if (!canManageForms) return
    const duplicated = await duplicateForm(form.id)
    if (!usesApiDataClient) {
      const persisted = getPersistentFormListState()
      setFormOrder(persisted.formOrder)
      setFolders(persisted.formFolders)
    }
    void queryClient.invalidateQueries({ queryKey: ['forms'] })
    void queryClient.invalidateQueries({ queryKey: ['form-folders'] })
    setActiveFolderId('all')
    setPublicationStatus('all')
    setArchiveStatus('active')
    setEditingFormId(duplicated.id)
  }

  async function createSelectedTemplateForm(
    template: (typeof formTemplates)[number],
    input: { title?: string; description?: string },
  ) {
    if (!canManageForms) return
    setCreatingTemplateId(template.id)
    try {
      const form = await createFormFromTemplate(template.id, input)
      void queryClient.invalidateQueries({ queryKey: ['forms'] })
      void queryClient.invalidateQueries({ queryKey: ['form-folders'] })
      setTemplatePickerOpen(false)
      setActiveFolderId('all')
      setPublicationStatus('all')
      setArchiveStatus('active')
      setEditingFormId(form.id)
    } finally {
      setCreatingTemplateId(null)
    }
  }

  async function changeSelectedFormStatus(
    form: FormUiContract,
    nextStatus: 'draft' | 'published' | 'archived',
  ) {
    if (!canManageForms) return
    const updated = await changeFormStatus(form.id, nextStatus)
    void queryClient.invalidateQueries({ queryKey: ['forms'] })
    setManagementDialog((current) =>
      current && current.form.id === form.id && updated
        ? {
            ...current,
            form: {
              ...current.form,
              status: updated.status,
              updatedAt: updated.updatedAt,
            },
          }
        : current,
    )
  }

  return (
    <div className="flex h-dvh min-h-0 overflow-hidden">
      <FormFolderSidebar
        forms={visibleForms}
        folders={folders}
        activeFolderId={activeFolderId}
        publicationStatus={publicationStatus}
        canManageForms={canManageForms}
        onSelectFolder={setActiveFolderId}
        onPublicationStatusChange={setPublicationStatus}
        onCreateFolder={createFolderFromSidebar}
        onUpdateFolder={updateFolderFromSidebar}
        onRemoveFolder={removeFolderFromSidebar}
        onReorderFolders={reorderFoldersFromSidebar}
      />
      <main className="min-w-0 flex-1 overflow-hidden">
        <ScrollArea className="h-full" viewportClassName="p-4 lg:p-6">
          <div className="mb-5 flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
            <div>
              <h1 className="text-2xl font-semibold">我的問卷</h1>
              <p className="mt-1 text-sm text-slate-500">
                {canManageForms
                  ? '搜尋、管理、分享與編輯所有問卷。'
                  : '搜尋、分享、預覽與查看相關客戶問卷清單。'}
              </p>
            </div>
            {canManageForms && (
              <div className="flex flex-wrap gap-2">
                {templatesEnabled ? (
                  <Button variant="outline" onClick={() => setTemplatePickerOpen(true)}>
                    <Sparkles className="size-4" />
                    使用範本
                  </Button>
                ) : null}
                <Button onClick={() => setEditingFormId(primaryFormId)}>
                  <Plus className="size-4" />
                  建立問卷
                </Button>
              </div>
            )}
          </div>

          <div className="mb-5 flex flex-col gap-3 rounded-lg border border-slate-200 bg-white p-3 md:flex-row md:items-center">
            <div className="relative min-w-0 flex-1">
              <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-slate-400" />
              <Input
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜尋問卷..."
                className="pl-9"
              />
            </div>
            <Select value={sort} onValueChange={(value) => setSort(value as FormListSort)}>
              <SelectTrigger className="md:w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="updated_desc">最近修改</SelectItem>
                <SelectItem value="responses_desc">最多回應</SelectItem>
                <SelectItem value="title_asc">字母順序</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={archiveStatus}
              onValueChange={(value) => setArchiveStatus(value as ArchiveStatusFilter)}
            >
              <SelectTrigger className="md:w-40">
                <SelectValue placeholder="封存狀態" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">未封存</SelectItem>
                <SelectItem value="archived">已封存</SelectItem>
                <SelectItem value="all">全部封存狀態</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex rounded-md border border-slate-200 bg-white p-1">
              <Button
                variant={view === 'grid' ? 'secondary' : 'ghost'}
                size="icon"
                aria-label="格狀檢視"
                onClick={() => setView('grid')}
              >
                <LayoutGrid className="size-4" />
              </Button>
              <Button
                variant={view === 'list' ? 'secondary' : 'ghost'}
                size="icon"
                aria-label="列表檢視"
                onClick={() => setView('list')}
              >
                <List className="size-4" />
              </Button>
            </div>
          </div>

          {filtered.length === 0 ? (
            <div className="grid min-h-80 place-items-center rounded-lg border border-dashed border-slate-300 bg-white text-center">
              <div>
                <FileText className="mx-auto size-10 text-slate-400" />
                <h2 className="mt-4 font-semibold">找不到相關問卷</h2>
                <p className="mt-1 text-sm text-slate-500">請調整搜尋條件或建立新問卷。</p>
              </div>
            </div>
          ) : view === 'grid' ? (
            <DndContext
              sensors={folderSensors}
              collisionDetection={closestCenter}
              onDragEnd={reorderForms}
            >
              <SortableContext
                items={filtered.map((form) => form.id)}
                strategy={rectSortingStrategy}
              >
                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                  {filtered.map((form) => (
                    <FormListCard
                      key={form.id}
                      form={form}
                      folderNames={folders
                        .filter((folder) => folder.formIds.includes(form.id))
                        .map((folder) => folder.name)}
                      folders={folders}
                      canManageForms={canManageForms}
                      onMoveToFolder={moveFormToFolder}
                      onManage={openFormManagement}
                      onDuplicate={(form) => void duplicateSelectedForm(form)}
                      onStatusChange={(form, nextStatus) =>
                        void changeSelectedFormStatus(form, nextStatus)
                      }
                      onPreview={previewForm}
                      onEdit={setEditingFormId}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          ) : (
            <DndContext
              sensors={folderSensors}
              collisionDetection={closestCenter}
              onDragEnd={reorderForms}
            >
              <SortableContext
                items={filtered.map((form) => form.id)}
                strategy={verticalListSortingStrategy}
              >
                <FormListTable
                  forms={filtered}
                  folders={folders}
                  canManageForms={canManageForms}
                  onMoveToFolder={moveFormToFolder}
                  onManage={openFormManagement}
                  onDuplicate={(form) => void duplicateSelectedForm(form)}
                  onStatusChange={(form, nextStatus) =>
                    void changeSelectedFormStatus(form, nextStatus)
                  }
                  onPreview={previewForm}
                  onEdit={setEditingFormId}
                />
              </SortableContext>
            </DndContext>
          )}
        </ScrollArea>
      </main>
      <FormBuilderDialog
        formId={editingFormId}
        open={Boolean(editingFormId)}
        onOpenChange={(open) => {
          if (!open) {
            setEditingFormId(null)
            void queryClient.invalidateQueries({ queryKey: ['forms'] })
            void queryClient.invalidateQueries({ queryKey: ['form-folders'] })
          }
        }}
      />
      <FormTemplatePickerDialog
        open={templatePickerOpen}
        onOpenChange={setTemplatePickerOpen}
        templates={formTemplates}
        isCreating={creatingTemplateId !== null}
        onCreateForm={createSelectedTemplateForm}
      />
      <FormManagementWorkspaceDialog
        form={managementDialog?.form ?? null}
        canManageForms={canManageForms}
        folders={
          managementDialog
            ? folders.filter((folder) => folder.formIds.includes(managementDialog.form.id))
            : []
        }
        open={Boolean(managementDialog)}
        activeTab={managementDialog?.tab}
        onOpenChange={(open) => {
          if (!open) setManagementDialog(null)
        }}
        onTabChange={(tab) => {
          setManagementDialog((current) => (current ? { ...current, tab } : current))
        }}
        onEdit={setEditingFormId}
        onPreview={previewForm}
        onArchiveToggle={(nextStatus) => {
          if (managementDialog) void changeSelectedFormStatus(managementDialog.form, nextStatus)
        }}
      />
    </div>
  )
}
